// Examiner ElasticSearch

import "whatwg-fetch";
import { all } from "awaity/esm";
import { optionsGen } from "../helpers/api";
import { toESQuery, toESDateHistogramQuery, toESGroupByQuery, toESDoubleGroupByQuery, toESTripleGroupByQuery } from "../helpers/query";
import { toTitleCase } from "../helpers/converters";
import { API_URL } from "../config";
import qs from "qs";

const AUTH = false;
export const PROSECUTION_ES_URL = `${API_URL}/patents/examiner/_search`;
export const LAWFIRM_URL = `${API_URL}/lawfirm`;

export function getDistinctEntities(val) {
  return fetch(
    PROSECUTION_ES_URL,
    optionsGen(
      "POST",
      toESGroupByQuery("examiner_name", {
        examiner_name: val
      })
    )
  )
    .then(resp => resp.json())
    .then(json => json.aggregations["examiner_name"].buckets.map(e => toTitleCase(e.key)));
}

export function getEntityApplications(name, field) {
  return fetch(
    PROSECUTION_ES_URL,
    optionsGen(
      "POST",
      toESQuery({
        [field]: `"${name}"`, // surround name with quotes to initiate exact matching search
        sort: "-filing_date",
        limit: 10
      })
    )
  ).then(resp => resp.json());
}

export function getEntityApplicationsByDate(query) {
  return all([
    fetch(
      PROSECUTION_ES_URL,
      optionsGen(
        "POST",
        toESDateHistogramQuery("patent_issue_date", "year", {
          [query.field]: `"${query.name}"`
        })
      )
    ).then(resp => resp.json()),
    fetch(
      PROSECUTION_ES_URL,
      optionsGen(
        "POST",
        toESDateHistogramQuery("abandon_date", "year", {
          [query.field]: `"${query.name}"`
        })
      )
    ).then(resp => resp.json())
  ]).then(([resID, resAD]) => {
    return [
      {
        k: "Issued",
        v: resID.aggregations.patent_issue_date.buckets.map(b => ({ k: b.key_as_string.substring(0, 4), v: b.doc_count }))
      },
      {
        k: "Abandoned",
        v: resAD.aggregations.abandon_date.buckets.map(b => ({ k: b.key_as_string.substring(0, 4), v: b.doc_count }))
      }
    ];
  });
}

export function getEntityAppCounts(name, field) {
  const query = toESGroupByQuery("disposal_type", {
    [field]: `"${name}"`
  });
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations.disposal_type.buckets.forEach(e => (res[e.key] = e.doc_count));

      return res;
    });
}

export function getTopEntitiesByAllowanceRate(field1, top = 30, query = {}, field2 = "disposal_type") {
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", toESDoubleGroupByQuery(field1, field2, query, top * 2)))
    .then(resp => resp.json())
    .then(json => {
      const getAllowanceRate = arr => {
        let issuedCnt = 0;
        let abandonedCnt = 0;
        arr.forEach(o => {
          if (o.key === "Issued") {
            issuedCnt = o.doc_count;
          }
          if (o.key === "Abandoned") {
            abandonedCnt = o.doc_count;
          }
        });
        return issuedCnt + abandonedCnt == 0 ? 0 : issuedCnt / (issuedCnt + abandonedCnt);
      };

      const results = json.aggregations[field1].buckets
        .map(o => ({ key: o.key, count: o.doc_count, allowanceRate: getAllowanceRate(o[field2].buckets) }))
        .sort((small, large) => large.allowanceRate - small.allowanceRate)
        .map((o, i) => ({ ...o, rank: i + 1 }));

      return results.slice(0, top);
    });
}

export function getTopLawFirmsByLPIX(top = 30, query = {}) {
  const { examiner_art_unit: au } = query;
  const queryStr = au ? qs.stringify({ au }, { arrayFormat: "repeat", addQueryPrefix: true }) : "";
  return fetch(`${LAWFIRM_URL}/lpix${queryStr}`, optionsGen("GET"))
    .then(resp => resp.json())
    .then(json => {
      const results = json
        .map(o => ({ key: o.lf, lpix: o.s, count: o.t }))
        .sort((small, large) => large.lpix - small.lpix)
        .map((o, i) => ({ ...o, rank: i + 1 }));
      return results.slice(0, top);
    });
}

export function getLawFirmsWithAttrsByArtUnit(artUnit) {
  return fetch(`${LAWFIRM_URL}/attrs/${artUnit}`, optionsGen("GET")).then(resp => resp.json());
}

export function getEntityAppCountsByOneCondition(name, condition = "examiner_art_unit", byArtUnit = false, field) {
  const query = byArtUnit
    ? toESTripleGroupByQuery(
        "examiner_art_unit",
        condition,
        "disposal_type",
        {
          [field]: `"${name}"`
        },
        500
      )
    : toESDoubleGroupByQuery(
        condition,
        "disposal_type",
        {
          [field]: `"${name}"`
        },
        500
      );
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      if (byArtUnit) {
        let res = {};
        json.aggregations.examiner_art_unit.buckets.forEach(b => {
          res[b.key] = {};
          b[condition].buckets.forEach(bib => {
            res[b.key][bib.key] = {};
            bib.disposal_type.buckets.forEach(bibib => (res[b.key][bib.key][bibib.key] = bibib.doc_count));
          });
        });
        return res;
      } else {
        let res = {};
        json.aggregations[condition].buckets.forEach(b => {
          res[b.key] = {};
          b.disposal_type.buckets.forEach(bib => (res[b.key][bib.key] = bib.doc_count));
        });
        return res;
      }
    });
}

export function getAppCountsByOneCondition(condition = "examiner_art_unit", byArtUnit = false) {
  const query = byArtUnit ? toESTripleGroupByQuery("examiner_art_unit", condition, "disposal_type", {}, 500) : toESDoubleGroupByQuery(condition, "disposal_type", {}, 500);
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      if (byArtUnit) {
        let res = {};
        json.aggregations.examiner_art_unit.buckets.forEach(b => {
          res[b.key] = {};
          b[condition].buckets.forEach(bib => {
            res[b.key][bib.key] = {};
            bib.disposal_type.buckets.forEach(bibib => (res[b.key][bib.key][bibib.key] = bibib.doc_count));
          });
        });
        return res;
      } else {
        let res = {};
        json.aggregations[condition].buckets.forEach(b => {
          res[b.key] = {};
          b.disposal_type.buckets.forEach(bib => (res[b.key][bib.key] = bib.doc_count));
        });
        return res;
      }
    });
}

export function getDistinctValues(field, queries) {
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", toESGroupByQuery(field, queries, 20), AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json.aggregations[field].buckets.map(e => toTitleCase(e.key));
    });
}
